/** Copyright (c) 2018 T. Rowe Price. All rights reserved. */

import './TransactionHistoryTable.scss';

import React, { Fragment } from 'react';
import { camelCase, kebabCase, isEmpty } from 'lodash';

import { Dollar, FormattedDate, Quantity } from 'components/shared/Text/Text';

import { MONEY_MARKET_FORMAT, NON_BREAKING_SPACE } from 'services/Formats';

import Content from 'services/Content';

import { getHistoricalTransactions } from 'services/Rest';

import {
  isActiveAdvisory,
  isActivePlus,
  isBrokerage,
  isCollegeSavings,
  isSelfManaged,
  isManaged,
  MUTUAL_FUND
} from 'services/Products';

import Optioner from 'components/shared/Optioner/Optioner';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

import TransactionInfoModal from 'components/layouts/Transaction/TransactionInfoModal';

import { TRANSACTION_DETAILS, RAS_FEE } from 'services/Transactions';
import ViewFeeDetailsModal from 'components/layouts/Transaction/ViewFeeDetailsModal';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';

const columns = [
  'Trade Date',
  'Transaction Detail',
  'Account Name',
  'Quantity',
  'Price',
  'Net Dollar Amount'
];

const collegeSavingsColumns = [
  'Trade Date',
  'Transaction Detail',
  'Account Name',
  'Unit Amount',
  'Price',
  'Transaction Amount'
];

const MAX_DAYS = 1095;

const TransactionHistoryHeaders = ({ columns = [] }) => (
  <tr className="columns">
    {columns.map((name) => (
      <th
        scope="col"
        className={`transactions-history-header-cell test-${camelCase(
          name
        )} transactions-history-${kebabCase(name)}`}
        key={name}>
        {name}
      </th>
    ))}
  </tr>
);

const TradeDate = ({ tradeDate = '' }) => (
  <FormattedDate
    element="td"
    className="transactions-history-cell transactions-history-trade-date">
    {tradeDate}
  </FormattedDate>
);

const TransactionDetail = ({
  costBasis,
  productType,
  transactionDescription,
  transactionType,
  transactionTypeId
}) => {
  let hasTransactionFeeModal;
  let transactionFeeModalDetail;

  if (isActiveAdvisory(productType) && transactionTypeId === RAS_FEE) {
    hasTransactionFeeModal = true;
    transactionFeeModalDetail = Content.retirementAdvisoryServiceFee;
  } else if (isBrokerage(productType) || isActiveAdvisory(productType)) {
    hasTransactionFeeModal = TRANSACTION_DETAILS.includes(transactionType);
    transactionFeeModalDetail = transactionType;
  } else {
    hasTransactionFeeModal = TRANSACTION_DETAILS.includes(
      transactionDescription
    );
    transactionFeeModalDetail = transactionDescription;
  }

  return (
    <td className="transactions-history-cell transactions-history-transaction-detail blocked_data">
      <div className="transactions-history-detail-type">
        {hasTransactionFeeModal && isSelfManaged(productType) && (
          <TransactionInfoModal detail={transactionFeeModalDetail} />
        )}
        {hasTransactionFeeModal && isManaged(productType) && (
          <Fragment>
            RETIREMENT ADVISORY SERVICE FEE
            <ViewFeeDetailsModal detail={transactionFeeModalDetail} />
          </Fragment>
        )}
        {!hasTransactionFeeModal &&
          (!isBrokerage(productType) &&
          !isActivePlus(productType) &&
          isSelfManaged(productType)
            ? transactionDescription
            : transactionType)}
      </div>
      {costBasis && <div>Cost Basis Method: {costBasis}</div>}

      {(isBrokerage(productType) ||
        isActivePlus(productType) || isActiveAdvisory(productType)) && (
        <div>{transactionDescription}</div>
      )}
    </td>
  );
};

const AccountNumberOwners = ({ accountWChecksum, owners }) => (
  <div>
    {accountWChecksum}
    {!isEmpty(owners) && (
      <Fragment>
        <span className="spacing-dot" />
        {owners}
      </Fragment>
    )}
  </div>
);

const AccountNumberOwnersWithPermissions =
  WithUserPermissions(AccountNumberOwners);

const AccountInfo = ({
  accountName,
  accountWChecksum,
  owners,
  productType,
  ticker,
  accountId,
}) => (
  <Optioner val={Boolean(accountName)}>
    {{
      [true]: (
        <td className="transactions-history-cell transactions-history-account-name">
        <span className="transactions-history-account-type">
          {accountName}
        </span>
          {NON_BREAKING_SPACE}
          {!isBrokerage(productType) &&
            !isActivePlus(productType) &&
            !isActiveAdvisory(productType) && (
              <span className="transactions-history-account-ticker">
              {ticker}
            </span>
            )}
          <AccountLabel accountId={accountId}/>
          <AccountNumberOwnersWithPermissions
            accountWChecksum={accountWChecksum}
            owners={owners}
            shouldMask
          />
        </td>
      ),
      _: <DefaultCell/>
    }}
  </Optioner>
);

const TransactionQuantity = ({ quantity = 0, productType }) => {
  if (!quantity) {
    return (
      <td className="transactions-history-cell transactions-history-quantity">
        {NON_BREAKING_SPACE}
      </td>
    );
  }

  return (
    <Quantity
      element="td"
      extraDecimal={isBrokerage(productType) || isActivePlus(productType)}
      className="transactions-history-cell transactions-history-quantity">
      {quantity}
    </Quantity>
  );
};

const Price = ({ price = 0.0, moneyMarket }) => {
  if (!price) {
    return (
      <td className="transactions-history-cell transactions-history-quantity">
        {NON_BREAKING_SPACE}
      </td>
    );
  }

  if (moneyMarket) {
    return (
      <Dollar
        element="td"
        format={MONEY_MARKET_FORMAT}
        className="transactions-history-cell transactions-history-price">
        {price}
      </Dollar>
    );
  }
  return (
    <Dollar
      element="td"
      className="transactions-history-cell transactions-history-price">
      {price}
    </Dollar>
  );
};

const NetDollarAmount = ({ netDollarAmount = 0.0 }) => (
  <Dollar
    element="td"
    className="transactions-history-cell transactions-history-net-dollar-amount">
    {netDollarAmount}
  </Dollar>
);

const DefaultCell = () => <td className="transactions-history-cell">-</td>;

const TransactionHistoryCells = ({ rowData, columns }) =>
  columns.map((colName, key) => {
    const col = colName === '' ? 'name' : camelCase(colName);
    return cellComponentMapper(col, rowData, key);
  });

const TransactionHistoryRows = ({ rows = [], columns, parentKey = '' }) => {
  return rows.map((row, childKey) => {
    return (
      <tr className="transactions-history-row" key={`${parentKey}:${childKey}`}>
        <TransactionHistoryCells rowData={row} columns={columns} />
      </tr>
    );
  });
};

const cellComponentMapper = (columnName, props, key) => {
  const mapper = {
    tradeDate: <TradeDate {...props} key={key} />,
    transactionDetail: <TransactionDetail {...props} key={key} />,
    accountName: <AccountInfo {...props} key={key} />,
    quantity: <TransactionQuantity {...props} key={key} />,
    price: <Price {...props} key={key} />,
    netDollarAmount: <NetDollarAmount {...props} key={key} />,
    unitAmount: <TransactionQuantity {...props} key={key} />,
    transactionAmount: <NetDollarAmount {...props} key={key} />
  };

  return mapper[columnName] || <DefaultCell />;
};

class TransactionHistoryTable extends React.Component {
  componentDidMount() {
    getHistoricalTransactions(MAX_DAYS);
  }

  render() {
    const {
      includeTransactionHistory,
      rows,
      productType,
      viewMoreLink,
      hasCollegeSavingsOnly
    } = this.props;
    const noTransactionsMessage = includeTransactionHistory
      ? 'You currently do not have any Historical Transactions.'
      : 'There is no transaction history for the filter criteria selected.';

    const transactionHistoryColumns =
      isCollegeSavings(productType) || hasCollegeSavingsOnly
        ? collegeSavingsColumns
        : columns;

    return (
      <Fragment>
        <div className="transactions-history-table-container module-container">
          {includeTransactionHistory && (
            <div className="account-details-historical-transactions">
              <h2 className="account-details-graph-header flex-header">
                Last Five Historical Transactions
                <a className="view-more-link" onClick={viewMoreLink}>
                  View Transaction History
                </a>
              </h2>
            </div>
          )}
          {isEmpty(rows) ? (
            <div className="transactions-history-no-transactions">
              {noTransactionsMessage}
            </div>
          ) : (
            <table className="transactions-history-table">
              <thead className="transactions-history-header">
                <TransactionHistoryHeaders
                  columns={transactionHistoryColumns}
                />
              </thead>
              <tbody>
                <TransactionHistoryRows
                  rows={rows}
                  columns={transactionHistoryColumns}
                />
              </tbody>
            </table>
          )}
        </div>
      </Fragment>
    );
  }
}

TransactionHistoryTable.defaultProps = {
  includeTransactionHistory: false,
  productType: MUTUAL_FUND,
  viewMoreLink: '',
  hasCollegeSavingsOnly: false
};

export default TransactionHistoryTable;
